import { useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  ClipboardList,
  BookOpen,
  Target,
  Clock,
  Loader2,
  AlertCircle,
  RotateCcw,
  TrendingUp,
  Play,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import {
  useSummary,
  useCategoryStats,
  useWeakQuestions,
  useProgress,
} from "@/hooks/use-analytics";
import { useCreateTest } from "@/hooks/use-tests";
import { getRandomQuestions } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";

export const Route = createFileRoute("/_main/exams/$slug/analytics")({
  component: AnalyticsPage,
});

export function AnalyticsPage() {
  const params = useParams({ strict: false }) as { slug: string };
  const examSlug = params.slug;
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const { data: summary, isLoading: summaryLoading } = useSummary(examSlug);
  const { data: categoryStats, isLoading: categoryLoading } = useCategoryStats(examSlug);
  const { data: weakQuestions, isLoading: weakLoading } = useWeakQuestions(examSlug, 20);
  const { data: progress, isLoading: progressLoading } = useProgress(examSlug, 30);
  const createTestMutation = useCreateTest(examSlug);

  const [retryingWeak, setRetryingWeak] = useState(false);
  const [retryingQuestion, setRetryingQuestion] = useState<number | null>(null);
  const [retryingCategory, setRetryingCategory] = useState<number | null>(null);

  async function handleRetryAllWeak() {
    if (!weakQuestions || weakQuestions.length === 0) return;
    setRetryingWeak(true);
    try {
      const ids = weakQuestions.map((q) => q.question_id);
      const { session, questions } = await createTestMutation.mutateAsync({
        question_ids: ids,
        mode: "practice",
      });
      localStorage.setItem(
        `test_${session.id}`,
        JSON.stringify({ session, questions }),
      );
      navigate({
        to: "/exams/$slug/test/$id",
        params: { slug: examSlug, id: session.id },
      });
    } catch {
      alert(t("common.error"));
    } finally {
      setRetryingWeak(false);
    }
  }

  async function handleRetrySingle(questionId: number) {
    setRetryingQuestion(questionId);
    try {
      const { session, questions } = await createTestMutation.mutateAsync({
        question_ids: [questionId],
        mode: "practice",
      });
      localStorage.setItem(
        `test_${session.id}`,
        JSON.stringify({ session, questions }),
      );
      navigate({
        to: "/exams/$slug/test/$id",
        params: { slug: examSlug, id: session.id },
      });
    } catch {
      alert(t("common.error"));
    } finally {
      setRetryingQuestion(null);
    }
  }

  async function handleRetryCategory(categoryId: number) {
    setRetryingCategory(categoryId);
    try {
      const questions = await getRandomQuestions(examSlug, 20, categoryId);
      if (questions.length === 0) {
        alert(t("common.no_data"));
        return;
      }
      const ids = questions.map((q) => q.id);
      const { session, questions: testQuestions } =
        await createTestMutation.mutateAsync({
          question_ids: ids,
          mode: "practice",
        });
      localStorage.setItem(
        `test_${session.id}`,
        JSON.stringify({ session, questions: testQuestions }),
      );
      navigate({
        to: "/exams/$slug/test/$id",
        params: { slug: examSlug, id: session.id },
      });
    } catch {
      alert(t("common.error"));
    } finally {
      setRetryingCategory(null);
    }
  }

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  function formatTime(ms: number): string {
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}${t("common.seconds_suffix")}`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}${t("common.minutes_suffix")} ${remainingSeconds}${t("common.seconds_suffix")}`;
  }

  function formatShortDate(dateStr: string): string {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  }

  const accuracyLabel = t("common.accuracy");
  const testCountLabel = t("analytics.daily_tests");

  const statsCards = summary
    ? [
        {
          title: t("analytics.total_tests"),
          value: `${summary.total_tests}${t("common.times_suffix")}`,
          icon: ClipboardList,
          iconColor: "text-blue-500",
          bgColor: "bg-blue-50 dark:bg-blue-950",
        },
        {
          title: t("analytics.questions_solved"),
          value: `${summary.total_answered}${t("common.questions")}`,
          icon: BookOpen,
          iconColor: "text-emerald-500",
          bgColor: "bg-emerald-50 dark:bg-emerald-950",
        },
        {
          title: t("common.accuracy"),
          value: `${Math.round(summary.accuracy_rate)}%`,
          icon: Target,
          iconColor: "text-amber-500",
          bgColor: "bg-amber-50 dark:bg-amber-950",
        },
        {
          title: t("analytics.avg_time"),
          value: formatTime(summary.avg_time_ms),
          icon: Clock,
          iconColor: "text-purple-500",
          bgColor: "bg-purple-50 dark:bg-purple-950",
        },
      ]
    : [];

  const recentProgressData = (progress ?? []).map((p) => ({
    date: formatShortDate(p.date),
    [accuracyLabel]: Math.round(p.accuracy_rate),
    [testCountLabel]: p.test_count,
  }));

  const categoryChartData = (categoryStats ?? []).map((c) => ({
    name:
      c.category_name.length > 10
        ? c.category_name.slice(0, 10) + "..."
        : c.category_name,
    fullName: c.category_name,
    [accuracyLabel]: Math.round(c.accuracy_rate),
    [t("analytics.solved_count")]: c.total_answered,
    [t("analytics.correct_count")]: c.correct_count,
  }));

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-3xl">
            {t("analytics.title")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            {t("analytics.desc")}
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview">
          <TabsList className="mb-6 w-full flex-wrap sm:w-auto">
            <TabsTrigger value="overview">{t("analytics.overview")}</TabsTrigger>
            <TabsTrigger value="categories">{t("analytics.by_category")}</TabsTrigger>
            <TabsTrigger value="weak">{t("analytics.weak_areas")}</TabsTrigger>
            <TabsTrigger value="trends">{t("analytics.progress")}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* Summary Stats */}
              {summaryLoading ? (
                <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                  {[...Array(4)].map((_, i) => (
                    <Card key={i}>
                      <CardContent className="py-4">
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 w-20 rounded bg-zinc-200 dark:bg-zinc-700" />
                          <div className="h-7 w-16 rounded bg-zinc-200 dark:bg-zinc-700" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                  {statsCards.map((stat) => (
                    <Card key={stat.title}>
                      <CardContent className="py-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                              {stat.title}
                            </p>
                            <p className="mt-1 text-xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-2xl">
                              {stat.value}
                            </p>
                          </div>
                          <div className={`rounded-lg p-2 ${stat.bgColor}`}>
                            <stat.icon
                              className={`h-5 w-5 ${stat.iconColor}`}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Recent Accuracy Trend */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-500" />
                    <CardTitle className="text-lg font-semibold">
                      {t("analytics.recent_accuracy")}
                    </CardTitle>
                  </div>
                  <CardDescription>
                    {t("analytics.recent_accuracy_desc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {progressLoading ? (
                    <div className="flex h-64 items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                    </div>
                  ) : recentProgressData.length === 0 ? (
                    <div className="flex h-64 flex-col items-center justify-center text-zinc-400">
                      <TrendingUp className="mb-2 h-8 w-8" />
                      <p className="text-sm">{t("common.no_data")}</p>
                    </div>
                  ) : (
                    <div className="h-64 w-full sm:h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={recentProgressData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e4e4e7"
                          />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            stroke="#a1a1aa"
                          />
                          <YAxis
                            domain={[0, 100]}
                            tick={{ fontSize: 12 }}
                            stroke="#a1a1aa"
                            unit="%"
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #e4e4e7",
                              borderRadius: "8px",
                              fontSize: "13px",
                            }}
                            formatter={(value) => [`${value}%`, accuracyLabel]}
                          />
                          <Line
                            type="monotone"
                            dataKey={accuracyLabel}
                            stroke="#3b82f6"
                            strokeWidth={2.5}
                            dot={{ r: 3, fill: "#3b82f6" }}
                            activeDot={{ r: 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* By Category Tab */}
          <TabsContent value="categories">
            <div className="space-y-6">
              {/* Category Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold">
                    {t("analytics.category_accuracy")}
                  </CardTitle>
                  <CardDescription>
                    {t("analytics.category_accuracy_desc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {categoryLoading ? (
                    <div className="flex h-72 items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                    </div>
                  ) : categoryChartData.length === 0 ? (
                    <div className="flex h-72 flex-col items-center justify-center text-zinc-400">
                      <BookOpen className="mb-2 h-8 w-8" />
                      <p className="text-sm">{t("common.no_data")}</p>
                    </div>
                  ) : (
                    <div className="h-72 w-full sm:h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={categoryChartData}
                          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e4e4e7"
                          />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11 }}
                            stroke="#a1a1aa"
                            angle={-20}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis
                            domain={[0, 100]}
                            tick={{ fontSize: 12 }}
                            stroke="#a1a1aa"
                            unit="%"
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #e4e4e7",
                              borderRadius: "8px",
                              fontSize: "13px",
                            }}
                            formatter={(value, name) => [
                              `${value}%`,
                              name,
                            ]}
                            labelFormatter={(label, payload) => {
                              if (payload && payload.length > 0) {
                                const item = payload[0]?.payload as { fullName?: string };
                                return item?.fullName ?? String(label);
                              }
                              return String(label);
                            }}
                          />
                          <Bar
                            dataKey={accuracyLabel}
                            fill="#3b82f6"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={50}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Category Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold">
                    {t("analytics.category_detail")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {categoryLoading ? (
                    <div className="space-y-3">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="animate-pulse">
                          <div className="h-12 rounded bg-zinc-100 dark:bg-zinc-800" />
                        </div>
                      ))}
                    </div>
                  ) : !categoryStats || categoryStats.length === 0 ? (
                    <p className="py-8 text-center text-sm text-zinc-400">
                      {t("common.no_data")}
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-zinc-200 dark:border-zinc-700">
                            <th className="pb-3 text-left font-semibold text-zinc-600 dark:text-zinc-400">
                              {t("common.category")}
                            </th>
                            <th className="pb-3 text-right font-semibold text-zinc-600 dark:text-zinc-400">
                              {t("analytics.solved_count")}
                            </th>
                            <th className="pb-3 text-right font-semibold text-zinc-600 dark:text-zinc-400">
                              {t("analytics.correct_count")}
                            </th>
                            <th className="pb-3 text-right font-semibold text-zinc-600 dark:text-zinc-400">
                              {t("common.accuracy")}
                            </th>
                            <th className="pb-3 text-right font-semibold text-zinc-600 dark:text-zinc-400">
                              {t("common.practice")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {categoryStats.map((cat) => {
                            const rate = Math.round(cat.accuracy_rate);
                            return (
                              <tr
                                key={cat.category_id}
                                className="border-b border-zinc-100 last:border-0 dark:border-zinc-800"
                              >
                                <td className="py-3 font-medium text-zinc-800 dark:text-zinc-200">
                                  {cat.category_name}
                                </td>
                                <td className="py-3 text-right text-zinc-600 dark:text-zinc-400">
                                  {cat.total_answered}
                                </td>
                                <td className="py-3 text-right text-zinc-600 dark:text-zinc-400">
                                  {cat.correct_count}
                                </td>
                                <td className="py-3 text-right">
                                  <Badge
                                    variant={
                                      rate >= 80
                                        ? "default"
                                        : rate >= 60
                                          ? "secondary"
                                          : "destructive"
                                    }
                                  >
                                    {rate}%
                                  </Badge>
                                </td>
                                <td className="py-3 text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    disabled={retryingCategory === cat.category_id}
                                    onClick={() => handleRetryCategory(cat.category_id)}
                                  >
                                    {retryingCategory === cat.category_id ? (
                                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                    ) : (
                                      <Play className="mr-1 h-3 w-3" />
                                    )}
                                    {t("common.practice")}
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Weak Areas Tab */}
          <TabsContent value="weak">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                      <CardTitle className="text-lg font-semibold">
                        {t("analytics.weak_questions")}
                      </CardTitle>
                    </div>
                    <CardDescription className="mt-1">
                      {t("analytics.weak_questions_desc")}
                    </CardDescription>
                  </div>
                  {weakQuestions && weakQuestions.length > 0 && (
                    <Button
                      onClick={handleRetryAllWeak}
                      disabled={retryingWeak}
                      size="sm"
                      className="shrink-0"
                    >
                      {retryingWeak ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="mr-1 h-4 w-4" />
                      )}
                      {t("analytics.weak_practice")} ({weakQuestions.length})
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {weakLoading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="rounded-lg border border-zinc-100 p-4 dark:border-zinc-800">
                          <div className="mb-2 h-4 w-40 rounded bg-zinc-200 dark:bg-zinc-700" />
                          <div className="mb-3 h-3 w-full rounded bg-zinc-100 dark:bg-zinc-800" />
                          <div className="h-2 w-full rounded bg-zinc-100 dark:bg-zinc-800" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !weakQuestions || weakQuestions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-zinc-400">
                    <Target className="mb-3 h-10 w-10" />
                    <p className="text-sm font-medium">
                      {t("analytics.no_weak")}
                    </p>
                    <p className="mt-1 text-xs">
                      {t("analytics.no_weak_desc")}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {weakQuestions.map((q) => {
                      const rate = Math.round(q.accuracy_rate);
                      return (
                        <div
                          key={q.question_id}
                          className="rounded-lg border border-zinc-100 p-4 transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900"
                        >
                          <div className="mb-2 flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
                                  {t("questions.question_num")}{q.display_number ?? q.question_number}
                                </span>
                                <Badge
                                  variant={
                                    rate >= 60
                                      ? "secondary"
                                      : "destructive"
                                  }
                                  className="text-[10px]"
                                >
                                  {rate}%
                                </Badge>
                              </div>
                              <p className="mt-1 line-clamp-2 text-sm text-zinc-500 dark:text-zinc-400">
                                {q.text_ko.slice(0, 120)}
                                {q.text_ko.length > 120 ? "..." : ""}
                              </p>
                            </div>
                          </div>

                          <div className="mb-3 flex items-center gap-4 text-xs text-zinc-400">
                            <span>{t("analytics.attempt_count")}: {q.attempt_count}{t("common.times_suffix")}</span>
                            <span>
                              {t("common.correct")}: {q.correct_count}/{q.attempt_count}
                            </span>
                          </div>

                          <div className="mb-3">
                            <Progress value={rate} max={100} />
                          </div>

                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                            disabled={retryingQuestion === q.question_id}
                            onClick={() => handleRetrySingle(q.question_id)}
                          >
                            {retryingQuestion === q.question_id ? (
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            ) : (
                              <RotateCcw className="mr-1 h-3 w-3" />
                            )}
                            {t("analytics.review_question")}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends">
            <div className="space-y-6">
              {/* Daily Accuracy Line Chart */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-500" />
                    <CardTitle className="text-lg font-semibold">
                      {t("analytics.daily_accuracy")}
                    </CardTitle>
                  </div>
                  <CardDescription>
                    {t("analytics.recent_accuracy_desc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {progressLoading ? (
                    <div className="flex h-72 items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                    </div>
                  ) : recentProgressData.length === 0 ? (
                    <div className="flex h-72 flex-col items-center justify-center text-zinc-400">
                      <TrendingUp className="mb-2 h-8 w-8" />
                      <p className="text-sm">{t("common.no_data")}</p>
                    </div>
                  ) : (
                    <div className="h-64 w-full sm:h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={recentProgressData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e4e4e7"
                          />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            stroke="#a1a1aa"
                          />
                          <YAxis
                            domain={[0, 100]}
                            tick={{ fontSize: 12 }}
                            stroke="#a1a1aa"
                            unit="%"
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #e4e4e7",
                              borderRadius: "8px",
                              fontSize: "13px",
                            }}
                            formatter={(value) => [`${value}%`, accuracyLabel]}
                          />
                          <Line
                            type="monotone"
                            dataKey={accuracyLabel}
                            stroke="#3b82f6"
                            strokeWidth={2.5}
                            dot={{ r: 3, fill: "#3b82f6" }}
                            activeDot={{ r: 6, stroke: "#3b82f6", strokeWidth: 2 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Daily Test Count Area Chart */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-emerald-500" />
                    <CardTitle className="text-lg font-semibold">
                      {t("analytics.daily_tests")}
                    </CardTitle>
                  </div>
                  <CardDescription>
                    {t("analytics.daily_tests_desc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {progressLoading ? (
                    <div className="flex h-72 items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                    </div>
                  ) : recentProgressData.length === 0 ? (
                    <div className="flex h-72 flex-col items-center justify-center text-zinc-400">
                      <ClipboardList className="mb-2 h-8 w-8" />
                      <p className="text-sm">{t("common.no_data")}</p>
                    </div>
                  ) : (
                    <div className="h-64 w-full sm:h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={recentProgressData}>
                          <defs>
                            <linearGradient
                              id="colorTestCount"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#10b981"
                                stopOpacity={0.3}
                              />
                              <stop
                                offset="95%"
                                stopColor="#10b981"
                                stopOpacity={0}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e4e4e7"
                          />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            stroke="#a1a1aa"
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            stroke="#a1a1aa"
                            allowDecimals={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #e4e4e7",
                              borderRadius: "8px",
                              fontSize: "13px",
                            }}
                            formatter={(value) => [
                              `${value}${t("common.times_suffix")}`,
                              testCountLabel,
                            ]}
                          />
                          <Area
                            type="monotone"
                            dataKey={testCountLabel}
                            stroke="#10b981"
                            strokeWidth={2}
                            fill="url(#colorTestCount)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
